import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import './styles/style.css'; // Custom CSS for slider
import { toast, ToastContainer } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactModal from 'react-modal';
import imageCompression from 'browser-image-compression';
import { useFamilyAdmin } from './hooks/useFamilyAdmin';
 

function EditPersonalInformation() {

   const { menu, toggleMobileMenu } = useAppLayout(); 

  const location = useLocation();
  const navigate = useNavigate();

  const { isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();

  const memberId = location.state?.memberId;

  const { userId, familyCode, isFamilyAdmin } = useFamilyAdmin();

  const queryClient = useQueryClient();

  const [message, setMessage] = useState('');
  
  const [profile, setProfile] = useState({
      name: '',
      last_name: '',
      surname: '',
      n_prefix: '',
      age_set: '',
      date_of_birth: '',
      date_of_death: '',
      gender: '',
      phone_number: '',
      phone_number_2: '',
      email: '',
      status: '',
      under_age: '',
      inactive: '',
      hometown: '',
      place_of_birth: '',
      religion: '',
      country_id: '',
      facebook: '',
      twitter: '',
      instagram: '',
      linkin: '',
      youtube: '',
      profile_image: ''
      });


    const {
      data,
      isLoading,
      isError,
    } = useQuery({
      queryKey: ['userProfileDetails', memberId],
      queryFn: async () => {
        if (!memberId) throw new Error("No memberId provided");
    
        const res = await axios.get(`${API_URL}/edit-personalinfo-settings-api?memberId=${memberId}`, {
          withCredentials: true,
        });
    
        return res.data;
      },
      enabled: !!memberId, // prevent fetch if memberId is undefined
      staleTime: 0,
      cacheTime: Infinity,
    });
    


   useEffect(() => {
      if (data) {
        if (data.Status === "Success") {
          const user = data.user;
    
          setProfile({
              name: user.name || '',
      last_name: user.last_name || '',
      surname: user.surname || '',
      n_prefix: user.n_prefix || '',
      age_set: user.age_set || '',
      date_of_birth: user.date_of_birth ? user.date_of_birth.substring(0, 10) : '',
      date_of_death: user.date_of_death || '',
      gender: user.gender || '',
      phone_number: user.phone_number || '',
      email: user.email || '',
      status: user.status || '',
      under_age: user.under_age || '',
      inactive: user.inactive || '',
      hometown: user.hometown || '',
      place_of_birth: user.place_of_birth || '',
      religion: user.religion || '',
      country_id: user.country_id || '',
      facebook: user.facebook || '',
      twitter: user.twitter || '',
      instagram: user.instagram || '',
      linkin: user.linkin || '',
      youtube: user.youtube || '',
      profile_image: user.profile_image || '',
          });
        } else {
          setMessage("Failed to load user data");
        }
      }
    }, [data]);


  // Handle input changes
  const handleChange = (e) => {
      const { name, value } = e.target;
      setProfile((prev) => ({
          ...prev,
          [name]: value
      }));
  };


const [isSubmitting, setIsSubmitting] = useState(false);
const [activeTab, setActiveTab] = useState('personal');
   
const [previewImage, setPreviewImage] = useState(null);

const handleImageChange = async (e) => {
const file = e.target.files[0];
if (!file) return;

try {
  const options = {
    maxSizeMB: 1,             // Max size (1MB)
    maxWidthOrHeight: 800,    // Resize to max 800px
    useWebWorker: true,       // Faster compression
  };

  // Compress the image
  const compressedFile = await imageCompression(file, options);

  // Generate preview (from compressed file)
  const reader = new FileReader();
  reader.onloadend = () => {
    setPreviewImage(reader.result); // Show preview
  };
  reader.readAsDataURL(compressedFile);

  // Update profile with the compressed file
  setProfile((prev) => ({
    ...prev,
    profile_image: compressedFile,
  }));

} catch (err) {
  console.error('Image compression error:', err);
  toast.error('Failed to compress image.');
}
};



const handleProfileUpdate = async () => {
const formData = new FormData();
const targetKB = 100;
setIsSubmitting(true);
// Compress image if new one is selected
if (profile.profile_image && typeof profile.profile_image !== 'string') {
  try {
    let quality = 0.9;
    let compressedFile = profile.profile_image;

    while (true) {
      const options = {
        maxSizeMB: targetKB / 1024,
        initialQuality: quality,
        useWebWorker: true,
      };

      compressedFile = await imageCompression(profile.profile_image, options);
      if (compressedFile.size / 1024 <= targetKB || quality <= 0.1) break;
      quality -= 0.1;
    }

    formData.append('image_name', compressedFile);
  } catch (err) {
    console.error('Image compression error:', err);
    toast.warn("Image compression failed. Uploading original image.");
    formData.append('profile_image', profile.profile_image);
  }
} else if (typeof profile.profile_image === 'string') {
  // Existing image name passed if no new image selected
  formData.append('profile_image', profile.profile_image);
}

// Append the rest of the profile fields except image
Object.keys(profile).forEach((key) => {
  if (key !== 'profile_image') {
    formData.append(key, profile[key]);
  }
});

try {
  const response = await axios.put(
    `${API_URL}/edit-personalinfo-settings-update-api?memberId=${memberId}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
    }
  );

  toast.success("Details updated successfully");
  navigate('/family-tree');
} catch (error) {
  toast.error("Error updating profile");
  console.error('Error updating profile:', error);
}finally {
  setIsSubmitting(false);
}
};





const {
  data: checkUserRole,
  isLoadingUserRole,
  isErrorUserRole,
} = useQuery({
  queryKey: ['userProfileCheckUserRoles', memberId],
  queryFn: async () => {
    if (!memberId) throw new Error("No memberId provided");

    const res = await axios.get(
      `${API_URL}/edit-personalinfo-check-user-roles-api?memberId=${memberId}`,
      { withCredentials: true }
    );

    return res.data;
  },
  enabled: !!memberId,
  staleTime: 0,
  cacheTime: Infinity,
});



const {
  data: checkNodes,
  isLoadingNodes,
  isErrorNodes,
} = useQuery({
  queryKey: ['userProfileCheckNodes', memberId],
  queryFn: async () => {
    if (!memberId) throw new Error("No memberId provided");

    const res = await axios.get(
      `${API_URL}/edit-personalinfo-check-nodes-api?memberId=${memberId}`,
      { withCredentials: true }
    );

    return res.data;
  },
  enabled: !!memberId,
  staleTime: 0,
  cacheTime: Infinity,
});


const handleDeleteMember = async () => {
  setIsSubmitting(true);

  try {
    const response = await axios.post(
      `${API_URL}/delete-member-added-to-tree-api?memberId=${memberId}`,
      {},
      { withCredentials: true }
    );

    if (response.data.Status === "Success") {
      toast.success("Details deleted successfully");
      navigate("/family-tree");
    } else {
      toast.error(response.data.Message || "Delete failed");
    }

  } catch (error) {
    toast.error("Error deleting profile");
    console.error("Error deleting profile:", error);
  } finally {
    setIsSubmitting(false);
  }
};





const displayName =
  `${profile.n_prefix ? `${profile.n_prefix} ` : ''}` +
  (profile.last_name?.trim() || profile.name?.trim() || 'Member');


    return (
         
        <div className={`main-wrapper ${menu ? 'slide-nav': ''}`}>
            <Header onMenuClick={(value) => toggleMobileMenu()} />
            <Sidebar />
        <ToastContainer />

         <div className="page-wrapper">
         <div className="content container-fluid">

         <div className="header-bar d-flex align-items-center justify-content-between px-3 py-2">

<button 
  onClick={() => navigate(-1)} 
  className="btn p-0 border-0 bg-transparent"
  aria-label="Back"
>
  <i className="bi bi-chevron-left fs-4"></i>
</button>


<h5 className="mb-0 fw-bold flex-grow-1 text-center">My Bio</h5>

<div style={{ width: "32px" }}></div>
</div>

            <div className="row mt-1">

            <div className="col-md-12">

            <div className="profile-contentx">

            <div className="image-section">
                <div className="image-container">
                  <div
                    className="avatar-shell"
                    onClick={() => document.getElementById('uploadImageInput').click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && document.getElementById('uploadImageInput').click()}
                    aria-label="Change profile photo"
                    title="Change profile photo"
                  >
                 
                 
                    <div className="avatar-ring">
                    
                      <div className="avatar-inner">
                        {previewImage || profile.profile_image ? (
                          <img
                            src={
                              previewImage ||
                              (profile.profile_image instanceof File
                                ? previewImage
                                : `${API_URL}/images/${profile.profile_image}`)
                            }
                            alt="Profile"
                            className="avatar-img"
                            onError={(e) => {
                              e.currentTarget.src = '/images/default-avatar.png';
                            }}
                          />
                        ) : (
                          <div className="avatar-placeholder">
                            <i className="fas fa-user"></i>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* compact camera badge */}
                    <span className="avatar-camera">
                      <i className="fas fa-camera"></i>
                    </span>
                  </div>

                  <input
                    id="uploadImageInput"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                </div>

                <h4 className="avatar-name">{displayName}</h4>
                <p className="avatar-hint"></p>
              </div>


              <div className="tab-navigation">
                <button
                  className={`tab-button ${activeTab === 'personal' ? 'active' : ''}`}
                  onClick={() => setActiveTab('personal')}
                >
       
                  Personal Info
                </button>
                <button
                  className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
                  onClick={() => setActiveTab('contact')}
                >
      
                  Contact & Location
                </button>
                <button
                  className={`tab-button ${activeTab === 'social' ? 'active' : ''}`}
                  onClick={() => setActiveTab('social')}
                >
      
                  Social Media
                </button>
              </div>

              <div className="form-section">
                
                 {/* Personal */}
                 {activeTab === 'personal' && (
                  <div className="tab-content">
                    <h3 className="section-title">
                      <i className="fas fa-user-circle"></i>
                      Personal Details
                    </h3>

                    <div className="form-grid">
                      <div className="form-row">
                        <Field
                          label="First Name *"
                          icon="fas fa-user"
                          name="name"
                          value={profile.name}
                          onChange={handleChange}
                          placeholder="Enter first name"
                        />
                        <Field
                          label="Last Name *"
                          icon="fas fa-user"
                          name="last_name"
                          value={profile.last_name}
                          onChange={handleChange}
                          placeholder="Enter last name"
                        />
                      </div>

                      <div className="form-row">
                        <Field
                          label="Surname"
                          icon="fas fa-tag"
                          name="surname"
                          value={profile.surname}
                          onChange={handleChange}
                          placeholder="Enter surname"
                        />
                        <Field
                          label="Prefix"
                          icon="fas fa-crown"
                          name="n_prefix"
                          value={profile.n_prefix}
                          onChange={handleChange}
                          placeholder="e.g., Mr., Mrs., Dr."
                        />
                      </div>

                      <div className="form-row">
                        <Field
                          label="Date of Birth"
                          icon="fas fa-calendar-alt"
                          name="date_of_birth"
                          value={profile.date_of_birth}
                          onChange={handleChange}
                          type="date"
                        />

                  {(profile.status === 1) && (
                    <>
                     <Field
                          label="Date of Death"
                          icon="fas fa-calendar-alt"
                          name="date_of_death"
                          value={profile.date_of_death}
                          onChange={handleChange}
                          type="date"
                        />
                        
                    </>
                    )}
                        
                      </div>

                      <div className="form-row">
                       
                        <Select
                          label="Relationship Status"
                          icon="fas fa-heart"
                          name="status"
                          value={profile.status}
                          onChange={handleChange}
                          options={['Single', 'Married', 'Divorced', 'Widowed']}
                        />
                      </div>

                      <div className="form-row">
                        <Field
                          label="Religious Beliefs"
                          icon="fas fa-pray"
                          name="religion"
                          value={profile.religion}
                          onChange={handleChange}
                          placeholder="Enter religion"
                        />
                        <div />
                      </div>
                      <div className="form-row">
                      <div className="form-check mb-2">
    <input
      className="form-check-input"
      type="checkbox"
      name="status"
      checked={profile.status === 1}
      onChange={(e) => setProfile((prev) => ({ ...prev, status: e.target.checked ? 1 : 0,
        inactive: 0,
        under_age: 0,
       }))}
      id="statusCheck"
    />
    <label className="form-check-label" htmlFor="statusCheck">
      Deceased
    </label>
  </div>

  <div className="form-check mb-2">
    <input
      className="form-check-input"
      type="checkbox"
      name="inactive"
      checked={profile.inactive === 1}
      onChange={(e) => setProfile((prev) => ({ ...prev, inactive: e.target.checked ? 1 : 0,
        status: 0,
        under_age: 0,
       }))}
      id="inactiveCheck"
    />
    <label className="form-check-label" htmlFor="inactiveCheck">
      Inactive
    </label>
  </div>

  <div className="form-check mb-2">
    <input
      className="form-check-input"
      type="checkbox"
      name="under_age"
      checked={profile.under_age === 1}
      onChange={(e) => setProfile((prev) => ({ ...prev, under_age: e.target.checked ? 1 : 0,
        inactive: 0,
        status: 0,
       }))}
      id="underAgeCheck"
    />
    <label className="form-check-label" htmlFor="underAgeCheck">
      Under-age
    </label>
  </div>
                      </div>

                    </div>
                  </div>
                )}

                 {/* Contact */}
                 {activeTab === 'contact' && (
                  <div className="tab-content">
                    <h3 className="section-title">
                      <i className="fas fa-address-book"></i>
                      Contact & Location
                    </h3>

                    <div className="form-grid">
                    {!(profile.status === 1 || profile.under_age === 1 || profile.inactive === 1) && (
                        <>
                      <div className="form-row">
                        <Field
                          label="Email Address *"
                          icon="fas fa-envelope"
                          name="email"
                          type="email"
                          value={profile.email}
                          onChange={handleChange}
                          placeholder="Enter email address"
                        />
                        <Field
                          label="Phone Number"
                          icon="fas fa-phone"
                          name="phone_number"
                          type="tel"
                          value={profile.phone_number}
                          onChange={handleChange}
                          placeholder="Enter phone number"
                        />
                      </div>
                      </>
                        )}

                      

                      <div className="form-row">
                        <Field
                          label="Place of Birth"
                          icon="fas fa-map-marker-alt"
                          name="place_of_birth"
                          value={profile.place_of_birth}
                          onChange={handleChange}
                          placeholder="Enter place of birth"
                        />
                        <Field
                          label="Hometown"
                          icon="fas fa-home"
                          name="hometown"
                          value={profile.hometown}
                          onChange={handleChange}
                          placeholder="Enter hometown"
                        />
                      </div>
                    </div>
                  </div>
                )}

             
                {activeTab === 'social' && (
                  <div className="tab-content">
                    <h3 className="section-title">
                      <i className="fas fa-hashtag"></i>
                      Social Media Profiles
                    </h3>

                    <div className="form-grid">
                      <div className="form-row">
                        <Field
                          label="Facebook"
                          icon="fab fa-facebook"
                          name="facebook"
                          value={profile.facebook}
                          onChange={handleChange}
                          placeholder="https://facebook.com/username"
                        />
                        <Field
                          label="Twitter"
                          icon="fab fa-twitter"
                          name="twitter"
                          value={profile.twitter}
                          onChange={handleChange}
                          placeholder="https://twitter.com/username"
                        />
                      </div>

                      <div className="form-row">
                        <Field
                          label="Instagram"
                          icon="fab fa-instagram"
                          name="instagram"
                          value={profile.instagram}
                          onChange={handleChange}
                          placeholder="https://instagram.com/username"
                        />
                        <Field
                          label="LinkedIn"
                          icon="fab fa-linkedin"
                          name="linkin"
                          value={profile.linkin}
                          onChange={handleChange}
                          placeholder="https://linkedin.com/in/username"
                        />
                      </div>

                      <div className="form-row">
                        <Field
                          label="YouTube"
                          icon="fab fa-youtube"
                          name="youtube"
                          value={profile.youtube}
                          onChange={handleChange}
                          placeholder="https://youtube.com/c/username"
                        />
                        <div />
                      </div>
                    </div>
                  </div>
                )}


                  <div className="action-buttons d-flex justify-content-between">

                  <button onClick={() => navigate(-1)} className="cancel-button" disabled={isSubmitting}>
                    <i className="fas fa-times"></i>
                    Cancel
                  </button>

                  {(checkUserRole?.canUpdate || isFamilyAdmin || userId === memberId) && (
                  <button
                    onClick={handleProfileUpdate}
                    className={`update-button ${isSubmitting ? 'loading' : ''}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="button-spinner"></div>Updating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>Update Profile
                      </>
                    )}
                  </button>
                  )}

                </div>


                <div className='mt-4 d-flex justify-content-center'>
                
                {(checkUserRole?.canDelete || isFamilyAdmin) && userId !== memberId && (
                 

                <button  className="btn btn-danger-grad btn-lgx d-block text-center "
                onClick={handleDeleteMember}
                 
                disabled={isSubmitting || checkNodes?.canDelete === false || isLoadingNodes}
                >
                      

                      {isSubmitting ? (
                      <>
                        <div className="button-spinner"></div>Deleting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash-alt me-2" />Delete this member
                      </>
                    )}

                      
                    </button>

                )}
                 
  
                </div>

                   {/* Note */}
  {checkNodes?.canDelete === false && (
    <small className="text-muted text-center mt-2">
      ⚠️ This member cannot be deleted because they have a child.
    </small>
  )}



              </div>


                </div>
                </div>

            </div>


           
      <style>{`
  :root{
    --brand:#75B437; --brand-600:#5ea030; --brand-700:#4a8b28; --brand-dark:#184A2C;
    --ink:#0f172a; --bg:#EBF4ED; --bg-soft:#F5FBF8;
  }
  .theme-light,.scrollPageContainer{background:var(--bg);}

  /* Loading */
  .loading-container{display:flex;justify-content:center;align-items:center;min-height:50vh;padding:2rem}
  .loading-spinner{text-align:center;background:#fff;border-radius:20px;padding:1.5rem 2rem;box-shadow:0 10px 30px rgba(0,0,0,.08)}
  .spinner{width:42px;height:42px;border:4px solid #e5e7eb;border-top:4px solid var(--brand);border-radius:50%;animation:spin 1s linear infinite;margin:0 auto .75rem}
  @keyframes spin{to{transform:rotate(360deg)}}

  /* Header */
  .profile-header{display:flex;align-items:center;padding:12px 0;margin-bottom:18px;border-bottom:1px solid #e5e7eb}
  .back-button{background:none;border:0;color:var(--brand-dark);font-size:1.2rem;width:44px;height:44px;border-radius:50%;display:flex;align-items:center;justify-content:center}
  .back-button:hover{background:#e8f2ea}
  .profile-header h1{margin:0;font-size:1.5rem;font-weight:600;color:#1f2937}
  .header-spacer{width:44px}

  .profile-content{max-width:960px;margin:0 auto}

  /* Avatar (matches sister/spouse) */
  .image-section{text-align:center;margin:12px 0 18px}
  .image-container{display:inline-block;position:relative}
  .avatar-shell{position:relative;display:inline-block;cursor:pointer;outline:none}
  .avatar-ring{
    width:128px;height:128px;border-radius:50%;
    border:3px solid var(--brand-dark);
    box-shadow:0 8px 18px rgba(0,0,0,.18);
    display:flex;align-items:center;justify-content:center;
    background:linear-gradient(145deg,#7fbf44 0%,#2e6b3c 100%);
  }
  .avatar-inner{
    width:116px;height:116px;border-radius:50%;
    overflow:hidden;background:linear-gradient(145deg,#7fbf44 0%,#2e6b3c 100%);
  }
  .avatar-img{width:100%;height:100%;object-fit:cover;display:block}
  .avatar-placeholder{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#ecfdf5}
  .avatar-placeholder i{font-size:32px;opacity:.85}
  .avatar-camera{
    position:absolute;right:-2px;bottom:-2px;width:28px;height:28px;
    border-radius:50%;background:var(--brand-dark);color:#fff;border:2px solid #fff;
    display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,.25)
  }
  .avatar-camera i{font-size:12px}
  .avatar-name{margin:.6rem 0 .15rem;font-weight:700;color:#111827}
  .avatar-hint{margin:0;color:#6b7280;font-size:.85rem}

  /* Tabs */
.tab-navigation {
  display: flex;
  background: #fff;
  border-radius: 10px;                     /* slightly smaller corners */
  padding: 4px;                            /* reduce space around */
  margin: 10px 0 14px;
  box-shadow: 0 6px 16px rgba(0,0,0,.06);  /* lighter shadow */
  gap: 4px;
}

.tab-button {
  flex: 1;
  padding: 0.55rem 0.6rem;                 /* was .9rem */
  border: 0;
  background: transparent;
  color: #64748b;
  border-radius: 8px;                      /* less rounded */
  font-weight: 600;
  font-size: 0.85rem;                      /* reduce text size */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .4rem;
  min-height: 38px;                        /* compact height */
}

.tab-button:hover {
  background: #f8fafc;
}

.tab-button.active {
  background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%);
  color: #fff;
  box-shadow: 0 3px 10px rgba(24,74,44,.25);
}


  /* Card */
  .form-section{background:#fff;border-radius:20px;padding:22px;box-shadow:0 12px 28px rgba(0,0,0,.08);min-height:480px}
  .tab-content{animation:fadeIn .25s ease}
  @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
  .section-title{display:flex;align-items:center;gap:.6rem;margin:0 0 14px;padding-bottom:8px;border-bottom:1px solid #e5e7eb;font-size:1.15rem}
  .section-title i{color:var(--brand-dark)}

  .form-grid{display:flex;flex-direction:column;gap:14px}
  .form-row{display:grid;grid-template-columns:1fr 1fr;gap:14px}
  .form-group{display:flex;flex-direction:column}
  .form-label{display:flex;align-items:center;margin-bottom:.55rem;font-weight:600;color:#374151}
  .form-label i{margin-right:.6rem;color:var(--brand-dark)}
  .form-input{padding:.9rem 1rem;border:2px solid #e2e8f0;border-radius:14px;background:#f8fafc;transition:.2s;color:#111827}
  .form-input:focus{outline:none;border-color:var(--brand);box-shadow:0 0 0 3px rgba(117,180,55,.2);background:#fff}
  .date-input::-webkit-calendar-picker-indicator{cursor:pointer;filter:invert(.35)}

  .action-buttons{display:flex;gap:8px;justify-content:center;margin-top:18px;padding-top:12px;border-top:1px solid #e5e7eb}
  .cancel-button,.update-button{padding:.9rem 1.4rem;border:0;border-radius:10px;font-weight:700;display:flex;align-items:center;gap:.16rem}
  .cancel-button{background:#f8fafc;color:#64748b;border:2px solid #e2e8f0}
  .cancel-button:hover{background:#f1f5f9}
  .update-button{background:linear-gradient(135deg,var(--brand) 0%,var(--brand-dark) 100%);color:#fff;box-shadow:0 8px 20px rgba(24,74,44,.3)}
  .update-button.loading{opacity:.85}
  .button-spinner{width:16px;height:16px;border:2px solid rgba(255,255,255,.35);border-top:2px solid #fff;border-radius:50%;animation:spin .9s linear infinite}

  .end-page-space{height:30px}

  @media (max-width: 768px){
    .form-row{grid-template-columns:1fr}
    .form-section{padding:18px}
    .avatar-ring{width:112px;height:112px}
    .avatar-inner{width:102px;height:102px}
  }


.header-bar {
  background: #fff;
  border-bottom: 1px solid #eee;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 2px 6px rgba(0,0,0,.05);
}



      `}</style>
            

      </div>
      </div>
  
  </div>
        
    );
}



function Field({ label, icon, name, value, onChange, type = 'text', placeholder }) {
  return (
    <div className="form-group">
      <label className="form-label">
        <i className={icon}></i>
        {label}
      </label>
      <input
        className="form-input"
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </div>
  );
}

function Select({ label, icon, name, value, onChange, options = [] }) {
  return (
    <div className="form-group">
      <label className="form-label">
        <i className={icon}></i>
        {label}
      </label>
      <select className="form-input" name={name} value={value} onChange={onChange}>
        <option value="">Select</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

export default EditPersonalInformation;
